import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { useAuth } from './AuthContext';
import { useFamily } from './FamilyContext';
import {
  subscribeToPets,
  createPet as svcCreatePet,
  updatePet as svcUpdatePet,
  softDeletePet as svcSoftDeletePet,
  movePetToGroup as svcMovePetToGroup,
  feedPet as svcFeedPet,
  refillDispenser as svcRefillDispenser,
  addMainStock as svcAddMainStock,
  addExtraBags as svcAddExtraBags,
  openBag as svcOpenBag,
  confirmEmpty as svcConfirmEmpty,
  syncPhysicalStock as svcSyncPhysicalStock,
  fetchPetLogs as svcFetchPetLogs,
  fetchFamilyLogs as svcFetchFamilyLogs,
} from '../services/petService';
import {
  uploadPetPhoto as svcUploadPetPhoto,
  deletePetPhoto as svcDeletePetPhoto,
} from '../services/storageService';
import { invalidatePetPhotoCache } from '../hooks/usePetPhoto';

export const PetsContext = createContext(null);

/**
 * Provider de mascotas del hogar activo. Se monta dentro de AppShell,
 * después de FamilyProvider.
 *
 * Estado:
 *  - pets: array de mascotas activas (no eliminadas)
 *  - status: 'idle' | 'loading' | 'ready' | 'error'
 *  - error: AppError | null
 *
 * Mutations exponen la API del service envuelta — los componentes
 * NUNCA importan petService directamente.
 */
export const PetsProvider = ({ children }) => {
  const { profile } = useAuth();
  const { family } = useFamily();
  const familyId = family?.id ?? null;

  const [pets, setPets] = useState([]);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);

  /**
   * Suscripción al hogar. Re-suscribe si cambia familyId
   * (caso real: usuario sale del hogar y entra a otro).
   */
  useEffect(() => {
    if (!familyId) {
      setPets([]);
      setStatus('idle');
      return;
    }

    setStatus('loading');
    const unsubscribe = subscribeToPets(
      familyId,
      (nextPets) => {
        // Orden estable: las más recientes primero, soft-deleted excluidas
        // (esto último ya lo hace el service, dejamos sort acá).
        const sorted = [...nextPets].sort((a, b) => {
          const aTime = a.createdAt?.toMillis?.() ?? 0;
          const bTime = b.createdAt?.toMillis?.() ?? 0;
          return bTime - aTime;
        });
        setPets(sorted);
        setStatus('ready');
        setError(null);
      },
      (err) => {
        setError(err);
        setStatus('error');
      }
    );

    return unsubscribe;
  }, [familyId]);

  // ===== Mutations =====
  // Cada mutation lanza el error del service hacia arriba para que la UI
  // muestre toast/banner. NO swallowing — silenciar errores es peor que
  // mostrarlos feo.

  const createPet = useCallback(
    async (input) => {
      if (!profile?.uid || !familyId) throw new Error('Sin sesión o sin hogar');
      if (!input?.feedingGroupId) {
        throw new Error('feedingGroupId requerido para crear la mascota');
      }
      return svcCreatePet({ ...input, uid: profile.uid, familyId });
    },
    [profile?.uid, familyId]
  );

  const updatePet = useCallback(
    async (petId, updates) => {
      if (!profile?.uid) throw new Error('Sin sesión');
      return svcUpdatePet({ uid: profile.uid, petId, updates });
    },
    [profile?.uid]
  );

  const softDeletePet = useCallback(
    async (petId) => {
      if (!profile?.uid) throw new Error('Sin sesión');
      return svcSoftDeletePet({ uid: profile.uid, petId });
    },
    [profile?.uid]
  );

  const movePetToGroup = useCallback(
    async (petId, newFeedingGroupId) => {
      if (!profile?.uid) throw new Error('Sin sesión');
      return svcMovePetToGroup({ uid: profile.uid, petId, newFeedingGroupId });
    },
    [profile?.uid]
  );

  const feedPet = useCallback(
    async (petId, { forceFeed = false } = {}) => {
      if (!profile?.uid) throw new Error('Sin sesión');
      return svcFeedPet({ uid: profile.uid, petId, forceFeed });
    },
    [profile?.uid]
  );

  // Las siguientes ops viven en el grupo (compartido entre mascotas).
  // El caller pasa feedingGroupId directamente — el contexto solo agrega uid.

  const refillDispenser = useCallback(
    async (feedingGroupId, dispenserId, amountGr, { wasEmpty = false } = {}) => {
      if (!profile?.uid) throw new Error('Sin sesión');
      return svcRefillDispenser({ uid: profile.uid, feedingGroupId, dispenserId, amountGr, wasEmpty });
    },
    [profile?.uid]
  );

  const addMainStock = useCallback(
    async (feedingGroupId, amountGr) => {
      if (!profile?.uid) throw new Error('Sin sesión');
      return svcAddMainStock({ uid: profile.uid, feedingGroupId, amountGr });
    },
    [profile?.uid]
  );

  const addExtraBags = useCallback(
    async (feedingGroupId, { count, bagCapacityGr } = {}) => {
      if (!profile?.uid) throw new Error('Sin sesión');
      return svcAddExtraBags({ uid: profile.uid, feedingGroupId, count, bagCapacityGr });
    },
    [profile?.uid]
  );

  const openBag = useCallback(
    async (feedingGroupId, { mode } = {}) => {
      if (!profile?.uid) throw new Error('Sin sesión');
      return svcOpenBag({ uid: profile.uid, feedingGroupId, mode });
    },
    [profile?.uid]
  );

  const confirmEmpty = useCallback(
    async (feedingGroupId, dispenserId) => {
      if (!profile?.uid) throw new Error('Sin sesión');
      return svcConfirmEmpty({ uid: profile.uid, feedingGroupId, dispenserId });
    },
    [profile?.uid]
  );

  // syncPhysicalStock: input puede traer { mainStockGr, dispenserId, dispenserStockGr, reason }
  const syncPhysicalStock = useCallback(
    async (feedingGroupId, input) => {
      if (!profile?.uid) throw new Error('Sin sesión');
      return svcSyncPhysicalStock({ uid: profile.uid, feedingGroupId, ...input });
    },
    [profile?.uid]
  );

  /**
   * Sube la foto al worker (Supabase Storage privado vía signed URL),
   * persiste `pet.photoExt` en Firestore, e invalida la cache de signed URLs
   * para que los componentes que muestran la foto re-fetcheen.
   *
   * Dos pasos no-atómicos: si el update del doc falla tras el upload, queda
   * un objeto en Supabase sin doc apuntándolo. El próximo upload sobreescribe
   * (el path es determinístico: familyId/petId.jpg).
   */
  const uploadPetPhoto = useCallback(
    async (petId, file) => {
      if (!profile?.uid) throw new Error('Sin sesión');
      if (!familyId) throw new Error('Sin hogar activo');
      const { ext } = await svcUploadPetPhoto({ petId, file });
      await svcUpdatePet({ uid: profile.uid, petId, updates: { photoExt: ext } });
      invalidatePetPhotoCache(petId);
      return { ext };
    },
    [profile?.uid, familyId]
  );

  const deletePetPhoto = useCallback(
    async (petId) => {
      if (!profile?.uid) throw new Error('Sin sesión');
      if (!familyId) throw new Error('Sin hogar activo');
      await svcDeletePetPhoto({ petId });
      await svcUpdatePet({ uid: profile.uid, petId, updates: { photoExt: null } });
      invalidatePetPhotoCache(petId);
    },
    [profile?.uid, familyId]
  );

  const getPetById = useCallback(
    (petId) => pets.find((p) => p.id === petId) ?? null,
    [pets]
  );

  /**
   * Fetch puntual de logs visibles desde el detalle de una mascota.
   * Internamente consulta por el feedingGroupId del pet, por lo que el
   * resultado incluye eventos de stock del grupo compartido (refill,
   * confirmEmpty, etc.) además de los eventos pet-specific (feedings).
   *
   * No es reactivo — el caller debe refetchear tras mutaciones.
   */
  const fetchPetLogs = useCallback(
    async (petId, options) => {
      if (!familyId) throw new Error('Sin hogar activo');
      const pet = pets.find((p) => p.id === petId);
      if (!pet?.feedingGroupId) {
        throw new Error('La mascota no está asignada a un grupo');
      }
      return svcFetchPetLogs(familyId, pet.feedingGroupId, options);
    },
    [familyId, pets]
  );

  /**
   * Bitácora global del hogar — paginada por cursor.
   * Usado por LogsTimelinePage (Fase 5).
   */
  const fetchFamilyLogs = useCallback(
    async (options) => {
      if (!familyId) throw new Error('Sin hogar activo');
      return svcFetchFamilyLogs(familyId, options);
    },
    [familyId]
  );

  const value = useMemo(
    () => ({
      pets,
      status,
      error,
      isLoading: status === 'loading',
      isEmpty: status === 'ready' && pets.length === 0,
      getPetById,
      createPet,
      updatePet,
      softDeletePet,
      movePetToGroup,
      feedPet,
      refillDispenser,
      addMainStock,
      addExtraBags,
      openBag,
      confirmEmpty,
      syncPhysicalStock,
      uploadPetPhoto,
      deletePetPhoto,
      fetchPetLogs,
      fetchFamilyLogs,
    }),
    [
      pets,
      status,
      error,
      getPetById,
      createPet,
      updatePet,
      softDeletePet,
      movePetToGroup,
      feedPet,
      refillDispenser,
      addMainStock,
      addExtraBags,
      openBag,
      confirmEmpty,
      syncPhysicalStock,
      uploadPetPhoto,
      deletePetPhoto,
      fetchPetLogs,
      fetchFamilyLogs,
    ]
  );

  return <PetsContext.Provider value={value}>{children}</PetsContext.Provider>;
};

export const usePets = () => {
  const ctx = useContext(PetsContext);
  if (!ctx) {
    throw new Error('usePets must be used within a PetsProvider');
  }
  return ctx;
};